import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { CachedImage } from '../CachedImage';

interface ImageOption {
  label: string;
  value: string;
  imageUrl: string;
  description?: string;
}

interface DropdownWithImagePreviewProps {
  options: ImageOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowCustomValue?: boolean;
  customValueLabel?: string;
  customButtonLabel?: string;
  customInputPlaceholder?: string;
}

export const DropdownWithImagePreview = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select an option...",
  disabled = false,
  className = "",
  allowCustomValue = false,
  customValueLabel = "Enter Custom Value",
  customButtonLabel = "Use Custom Value", 
  customInputPlaceholder = "Type your custom value..."
}: DropdownWithImagePreviewProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search term
  const filteredOptions = options.filter(option => 
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Find selected option
  const selectedOption = options.find(option => option.value === value);
  
  // Check if current value is a custom value (not in predefined options)
  const isCurrentValueCustom = value && !selectedOption && allowCustomValue;

  const handleSelect = (option: ImageOption) => {
    onChange(option.value);
    setIsCustomMode(false);
    setCustomValue('');
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCustomValueSubmit = () => {
    if (customValue.trim()) {
      onChange(customValue.trim());
      setIsOpen(false);
      setIsCustomMode(false);
      setSearchTerm('');
    }
  };

  const handleCustomValueKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCustomValueSubmit();
    } else if (e.key === 'Escape') {
      setIsCustomMode(false);
      setCustomValue('');
    }
  };

  const enableCustomMode = () => {
    setIsCustomMode(true);
    setCustomValue(isCurrentValueCustom ? value : '');
    setSearchTerm('');
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected Value Display */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`w-full bg-elevated border border-subtle rounded-xl px-4 py-3 text-left transition-all flex items-center justify-between ${
          disabled 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:border-brand/50 focus:border-brand focus:outline-none cursor-pointer'
        }`}
      >
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {selectedOption ? (
            <>
              <div className="w-8 h-8 rounded overflow-hidden bg-surface flex-shrink-0">
                <CachedImage
                  src={selectedOption.imageUrl}
                  alt={selectedOption.label}
                  className="w-full h-full object-cover"
                  fallback={
                    <div className="w-full h-full flex items-center justify-center bg-elevated">
                      <div className="w-4 h-4 bg-textSecondary/30 rounded"></div>
                    </div>
                  }
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-th-primary font-medium truncate">
                  {selectedOption.label}
                </p>
                {selectedOption.description && (
                  <p className="text-th-secondary text-xs truncate">
                    {selectedOption.description}
                  </p>
                )}
              </div>
            </>
          ) : isCurrentValueCustom ? (
            <>
              <div className="w-8 h-8 rounded bg-brand/20 flex items-center justify-center flex-shrink-0">
                <span className="text-brand text-xs font-bold">C</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-th-primary font-medium truncate">
                  Custom Value
                </p>
                <p className="text-th-secondary text-xs truncate">
                  {value}
                </p>
              </div>
            </>
          ) : (
            <span className="text-th-secondary">{placeholder}</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-th-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div 
            className="fixed inset-0 bg-black/20 z-40 lg:hidden"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Options List */}
          <div className="absolute top-full left-0 right-0 mt-2 bg-elevated border border-subtle rounded-xl shadow-2xl z-50 max-h-80 overflow-hidden">
            {/* Search Input */}
            {options.length > 5 && (
              <div className="p-3 border-b border-subtle">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search options..."
                  className="w-full bg-elevated border border-subtle rounded-lg px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm"
                  autoFocus
                />
              </div>
            )}

            {/* Options */}
            <div className="max-h-64 overflow-y-auto">
              {/* Custom Value Input Mode */}
              {isCustomMode ? (
                <div className="p-4 border-b border-subtle">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-th-primary">
                      <div className="w-4 h-4 rounded bg-brand/20 flex items-center justify-center">
                        <span className="text-brand text-xs font-bold">C</span>
                      </div>
                      <span className="font-medium text-sm">{customValueLabel}</span>
                    </div>
                    <input
                      type="text"
                      value={customValue}
                      onChange={(e) => setCustomValue(e.target.value)}
                      onKeyDown={handleCustomValueKeyPress}
                      placeholder={customInputPlaceholder}
                      className="w-full bg-elevated border border-subtle rounded-lg px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm"
                      autoFocus
                    />
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleCustomValueSubmit}
                        disabled={!customValue.trim()}
                        className="flex-1 px-3 py-2 bg-brand text-surface rounded-lg text-sm font-medium hover:bg-brand/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {customButtonLabel}
                      </button>
                      <button
                        onClick={() => setIsCustomMode(false)}
                        className="px-3 py-2 bg-elevated border border-subtle text-th-secondary rounded-lg text-sm hover:text-th-primary transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                    <p className="text-th-secondary text-xs">
                      Press Enter to apply or Escape to cancel
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Custom Value Option */}
                  {allowCustomValue && (
                    <button
                      onClick={enableCustomMode}
                      className="w-full flex items-center space-x-3 p-3 text-left hover:bg-overlay transition-all group border-b border-subtle"
                    >
                      <div className="w-12 h-12 rounded-lg bg-brand/20 border-2 border-dashed border-brand/40 flex items-center justify-center flex-shrink-0 group-hover:border-brand/60 transition-all">
                        <span className="text-brand text-lg font-bold">+</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-brand group-hover:text-brand/80 transition-colors">
                          {customValueLabel}
                        </p>
                        <p className="text-th-secondary text-xs group-hover:text-th-primary/80 transition-colors">
                          Type your own custom value
                        </p>
                      </div>
                    </button>
                  )}
                </>
              )}
              
              {filteredOptions.length === 0 ? (
                <div className="p-4 text-center text-th-secondary">{!isCustomMode && (
                  <p className="text-sm">No options found</p>
                )}</div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleSelect(option)}
                    className={`w-full flex items-center space-x-3 p-3 text-left hover:bg-overlay transition-all group ${
                      value === option.value ? 'bg-brand/10 border-l-2 border-l-brand' : ''
                    }`}
                  >
                    {/* Image Preview */}
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-surface flex-shrink-0 ring-2 ring-transparent group-hover:ring-brand/30 transition-all">
                      <CachedImage
                        src={option.imageUrl}
                        alt={option.label}
                        className="w-full h-full object-cover"
                        fallback={
                          <div className="w-full h-full flex items-center justify-center bg-elevated">
                            <div className="w-6 h-6 bg-textSecondary/30 rounded"></div>
                          </div>
                        }
                      />
                    </div>

                    {/* Text Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`font-medium truncate ${
                          value === option.value ? 'text-brand' : 'text-th-primary group-hover:text-brand'
                        }`}>
                          {option.label}
                        </p>
                        {value === option.value && (
                          <Check className="w-4 h-4 text-brand flex-shrink-0" />
                        )}
                      </div>
                      {option.description && (
                        <p className="text-th-secondary text-xs truncate mt-1 group-hover:text-th-primary/80">
                          {option.description}
                        </p>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};